/**
 * A tela de atendimento — as três colunas da §11.2 (H04).
 *
 * *"É a tela que justifica o produto existir."*
 *
 * ── Uma requisição pinta o centro e a direita ─────────────────────────────
 *
 * A Técnica da task: *"três chamadas encadeadas fazem as colunas aparecerem em
 * momentos diferentes, o que parece lentidão mesmo com boa latência"*. Por isso
 * a C24 compõe tudo no servidor, e aqui uma chamada só devolve conversa,
 * mensagens, vínculo, cliente e oportunidades.
 *
 * A lista é a outra chamada, e tem de ser: ela sobrevive ao resto — serve-se
 * dos read models e continua de pé com o Chatwoot fora (§14.3).
 *
 * ── O tempo real é notificação, nunca dado ────────────────────────────────
 *
 * A tela assina a conversa aberta e as caixas que está mostrando. Quando o SSE
 * avisa, o cache é invalidado e a busca refeita (H02). Aplicar o evento direto
 * na lista dessincronizaria com o servidor — e a §11 é clara sobre onde a
 * autorização vive.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useObservarRecurso, useObservarRecursos } from '@/shared/api/assinaturas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useMarcarLida } from '@/features/atendimento/marcar-como-lida';
import { useTelaEstreita } from '@/shared/ui/tela';
import { chaves } from '@/shared/api/chaves';
import { useQuery } from '@tanstack/react-query';
import { useApi, useFunis, useInbox } from '@/shared/api/consultas';
import { useInvalidar } from '@/shared/api/provedor';
import { Vazio } from '@/shared/ui/estados/estados';
import { ColunaDaConversa } from './conversa';
import { useHistorico } from '../model/historico';
import { useEnvio } from '../model/usar-envio';
import { useEncerramento } from '../model/usar-encerramento';
import { FILTROS_VAZIOS, Lista, type FiltrosDaInbox } from './lista';
import { Painel } from './painel';
import './atendimento.css';
import { Etapas } from '@/shared/ui';
import { NovaTarefa } from '@/features/comercial/criar-tarefa';
import { AvisoDeOportunidadeExistente } from './aviso-de-oportunidade-existente';
import { DialogoDeFunil, funilSugerido } from './dialogo-de-funil';
import { lugarDaOportunidade } from '../lib/lugar-da-oportunidade';

/** Nova chave por comando: repetir a MESMA devolveria a resposta anterior (B30). */
const novaChave = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `cmd-${Date.now()}-${Math.random()}`;

export function Atendimento({
  conversaEscolhida,
  aoEscolher,
  aoAbrirCliente,
}: {
  /**
   * A conversa selecionada vive na casca, não aqui.
   *
   * Abrir da fila (H07) precisa trocar de tela E selecionar a conversa. Com o
   * estado dentro desta tela, o clique na fila mudaria de aba e abriria vazio —
   * porque este componente teria acabado de montar.
   */
  conversaEscolhida: string | null;
  aoEscolher: (id: string | null) => void;
  /** Abre o Cliente 360 (H08) — a casca troca de área. */
  aoAbrirCliente: (clienteId: string) => void;
}): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();

  const [filtros, definirFiltros] = useState<FiltrosDaInbox>(FILTROS_VAZIOS);
  const selecionada = conversaEscolhida;

  /**
   * No celular, as três colunas viram três etapas (H15, §11.1).
   *
   * A §11.1 estabelece a prioridade: *"responsividade prioriza inbox e cliente;
   * Kanban pode usar navegação horizontal controlada"*. Não é preciso que tudo
   * funcione igual — é preciso que atender e consultar cliente funcionem bem.
   *
   * Empilhar as três num rolo só, que é o que o CSS fazia, obriga a rolar a
   * lista inteira para chegar na conversa e a conversa inteira para chegar no
   * contexto. Em pé, num aparelho, isso é inutilizável.
   */
  const estreita = useTelaEstreita();
  const [etapa, definirEtapa] = useState<'lista' | 'conversa' | 'contexto'>('lista');

  /**
   * Escolher conversa leva à conversa; largar a conversa volta à lista.
   *
   * A navegação segue a seleção em vez de ter botão próprio: um passo a menos, e
   * o gesto de tocar na conversa já significa "quero ver esta".
   */
  const escolherConversa = useCallback(
    (id: string | null) => {
      aoEscolher(id);
      definirEtapa(id ? 'conversa' : 'lista');
    },
    [aoEscolher],
  );
  const [ocupado, definirOcupado] = useState(false);
  const [recado, definirRecado] = useState<string | null>(null);
  const [criandoTarefa, definirCriandoTarefa] = useState(false);

  // Campo vazio não vira `?situacao=`: o servidor recusaria o valor vazio, e
  // mandar a chave sem valor é diferente de não filtrar.
  const consultaDaInbox = useInbox(
    Object.fromEntries(Object.entries(filtros).filter(([, valor]) => valor !== '')),
  );

  const historico = useHistorico(selecionada);

  /*
   * Abrir a conversa diz ao motor que ela foi vista.
   *
   * Fica aqui, e não dentro do componente da conversa, porque depende do
   * `historico` — que é quem sabe quantas estavam por ler. Ver o cabeçalho de
   * `usar-marcar-lida.ts`.
   */
  useMarcarLida(api, selecionada, historico.conversa?.conversa.nao_lidas);

  // ── Tempo real ──────────────────────────────────────────────────────────
  useObservarRecurso(selecionada ? `conversation:${selecionada}` : null);

  const caixas = useMemo(
    () => [
      ...new Set(
        (consultaDaInbox.data?.itens ?? []).map((item) => `inbox:${item.caixa.referencia_externa}`),
      ),
    ],
    [consultaDaInbox.data],
  );
  useObservarRecursos(caixas);

  // ── Comandos ────────────────────────────────────────────────────────────

  // O envio virou fila (H06): o que falhou continua na tela com o texto e a
  // MESMA chave de idempotência, esperando o retry.
  const envio = useEnvio(api, selecionada, invalidar);

  /*
   * O encerramento precisa da situação PROJETADA para saber quando parar de
   * esperar (D08). Ela sai da mesma conversa composta que o cabeçalho já usa —
   * ler de outro lugar faria o botão e o selo ao lado dele discordarem por um
   * quadro.
   */
  const encerramento = useEncerramento(
    api,
    selecionada,
    historico.conversa?.conversa.situacao ?? null,
    invalidar,
  );

  const conversa = historico.conversa;

  /*
   * O funil sugerido é o da regra do número por onde esta conversa chegou
   * (roteamento, 24/09/2026). A regra pode ter também a etapa de entrada, e
   * ela vale se a pessoa ficar com o funil sugerido.
   */
  const funis = useFunis();
  const roteamento = useQuery({
    queryKey: chaves.roteamentoDeFunil(),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.roteamentoDeFunil({ sinal: signal }),
  });
  const regraDoNumero = roteamento.data?.itens.find(
    (c) => c.caixa === conversa?.conversa.caixa_referencia_externa,
  )?.regra;
  const listaDeFunis = funis.data?.itens ?? [];
  const sugerido = funilSugerido(listaDeFunis, regraDoNumero?.funil);
  const [escolhendoFunil, definirEscolhendoFunil] = useState(false);

  /*
   * As etapas de todos os funis, para o aviso dizer em que funil e etapa está a
   * oportunidade que o cliente já tem. Só busca quando há o que dizer.
   */
  const temOportunidade = (conversa?.oportunidades.length ?? 0) > 0;
  const etapas = useQuery({
    queryKey: chaves.etapasDoFunil(),
    enabled: api !== null && temOportunidade,
    queryFn: ({ signal }) => api!.etapasDoFunil({ sinal: signal }),
  });
  const [confirmandoOutra, definirConfirmandoOutra] = useState(false);

  const criarOportunidade = useCallback(
    async (funil: string | null) => {
      if (!api || !conversa?.cliente) return;

      const etapa =
        funil !== null && funil === regraDoNumero?.funil ? regraDoNumero.etapa_inicial : null;

      definirOcupado(true);
      definirRecado(null);
      try {
        await api.criarOportunidade({
          corpo: {
            nome: conversa.cliente.nome_exibicao ?? 'Oportunidade do atendimento',
            cliente_id: conversa.cliente.id,
            // Sem funil, o servidor usa o padrão: é o caso de um funil só.
            ...(etapa ? { etapa } : funil ? { funil } : {}),
          },
          idempotencyKey: novaChave(),
        });
        definirEscolhendoFunil(false);
        // O funil e a conversa mudam: a oportunidade nova aparece no painel
        // direito e no board.
        invalidar([chaves.funil(), chaves.conversa(conversa.conversa.id), chaves.inbox()]);
        // Sem funil escolhido, o servidor usou o padrão, que é o único quando há um só.
        const ondeEntrou =
          listaDeFunis.find((f) => f.chave === funil)?.nome ??
          (listaDeFunis.length === 1 ? listaDeFunis[0]?.nome : undefined);
        definirRecado(
          ondeEntrou ? `Oportunidade criada no funil "${ondeEntrou}".` : 'Oportunidade criada.',
        );
      } catch (erro: unknown) {
        definirRecado(recadoDeErro(erro, { generica: 'Não foi possível criar a oportunidade.' }));
      } finally {
        definirOcupado(false);
      }
    },
    [api, conversa, invalidar, regraDoNumero, listaDeFunis],
  );

  /*
   * Com um funil só não há o que perguntar: cria direto, como sempre foi. A
   * pergunta só existe quando há escolha a fazer.
   */
  const seguirParaCriar = useCallback(() => {
    if (listaDeFunis.length > 1 && sugerido) definirEscolhendoFunil(true);
    else void criarOportunidade(null);
  }, [listaDeFunis.length, sugerido, criarOportunidade]);

  // O cliente já tem oportunidade: antes, a pergunta "Criar outra?" (21/10/2026).
  const pedirOportunidade = useCallback(() => {
    if (temOportunidade) definirConfirmandoOutra(true);
    else seguirParaCriar();
  }, [temOportunidade, seguirParaCriar]);

  // Em tela larga as três aparecem juntas; em estreita, só a etapa atual.
  const mostrar = (qual: 'lista' | 'conversa' | 'contexto') => !estreita || etapa === qual;

  const [painelFechado, alternarPainel] = usePainelRecolhido();
  // O agendamento pertence à conversa em que abriu: trocar de conversa o fecha.
  const [agendandoEm, definirAgendandoEm] = useState<string | null>(null);
  const agendando =
    selecionada !== null && agendandoEm === selecionada && (estreita || !painelFechado);
  /*
   * O texto que o "Copiar" dos preços manda para a caixa de mensagem (#1085).
   * Vale só para a conversa em que nasceu; o editor o consome e avisa, e aí
   * ele some, para não voltar ao trocar de conversa e voltar.
   */
  const [paraInserir, definirParaInserir] = useState<{
    readonly conversaId: string;
    readonly texto: string;
  } | null>(null);

  return (
    <div
      className="atendimento"
      data-estreita={estreita ? 'sim' : undefined}
      data-painel={!estreita && painelFechado ? 'fechado' : undefined}
      data-agendando={agendando ? 'sim' : undefined}
    >
      {estreita && (
        /*
         * "Cliente" e "Conversa" ficam desabilitadas sem conversa escolhida,
         * porque não há contexto de quem. A §11.1 pede o painel direito a um
         * toque: é ele que diferencia o produto, e escondê-lo atrás de dois
         * gestos o esconde de verdade.
         */
        <Etapas
          rotulo="Partes do atendimento"
          atual={etapa}
          aoIr={definirEtapa}
          abas={[
            { chave: 'lista', nome: 'Conversas' },
            {
              chave: 'conversa',
              nome: 'Conversa',
              ativa: selecionada !== null,
              motivo: 'Escolha uma conversa primeiro',
            },
            {
              chave: 'contexto',
              nome: 'Cliente',
              ativa: selecionada !== null,
              motivo: 'Escolha uma conversa primeiro',
            },
          ]}
        />
      )}

      {mostrar('lista') && (
        <Lista
          consulta={consultaDaInbox}
          filtros={filtros}
          aoFiltrar={definirFiltros}
          selecionada={selecionada}
          aoSelecionar={escolherConversa}
        />
      )}

      {mostrar('conversa') &&
        (selecionada ? (
          <ColunaDaConversa
            historico={historico}
            envios={envio.envios}
            encerramento={encerramento}
            aoEnviar={envio.enviar}
            aoRepetir={envio.repetir}
            aoDescartar={envio.descartar}
            aoVoltar={estreita ? () => definirEtapa('lista') : undefined}
            paraInserir={paraInserir?.conversaId === selecionada ? paraInserir.texto : null}
            aoInserir={() => definirParaInserir(null)}
          />
        ) : (
          <section className="col col--conversa col--centrada">
            <Vazio
              titulo="Escolha uma conversa"
              porque="A conversa aparece aqui, com o histórico e o contexto do cliente ao lado."
            />
          </section>
        ))}

      {mostrar('contexto') && (
        <Painel
          conversa={conversa}
          aoCriarOportunidade={pedirOportunidade}
          aoCriarTarefa={() => definirCriandoTarefa(true)}
          aoAbrirCliente={aoAbrirCliente}
          ocupado={ocupado}
          fechado={painelFechado}
          agendando={agendando}
          aoAgendando={(sim) => definirAgendandoEm(sim ? selecionada : null)}
          aoCopiarParaMensagem={(texto) => {
            if (!selecionada) return;
            definirParaInserir({ conversaId: selecionada, texto });
            // No celular o painel é uma etapa inteira: volta para a conversa, com o texto na caixa.
            if (estreita) definirEtapa('conversa');
          }}
          {...(estreita ? {} : { aoAlternar: alternarPainel })}
        />
      )}

      {confirmandoOutra && conversa && (
        <AvisoDeOportunidadeExistente
          oportunidades={conversa.oportunidades.map((oportunidade) => ({
            id: oportunidade.id,
            titulo: oportunidade.titulo,
            lugar: lugarDaOportunidade(oportunidade.etapa, etapas.data?.itens ?? [], listaDeFunis),
          }))}
          aoCriarOutra={() => {
            definirConfirmandoOutra(false);
            seguirParaCriar();
          }}
          aoFechar={() => definirConfirmandoOutra(false)}
        />
      )}

      {escolhendoFunil && sugerido && (
        <DialogoDeFunil
          funis={listaDeFunis}
          sugerido={sugerido}
          ocupado={ocupado}
          aoCriar={(funil) => void criarOportunidade(funil)}
          aoFechar={() => definirEscolhendoFunil(false)}
        />
      )}

      {criandoTarefa && conversa?.cliente && (
        /*
          O formulário, com o cliente DESTA conversa já preenchido.

          Antes o botão criava num clique uma tarefa de título fixo, sem prazo e
          sem responsável — e era essa a metade que importava. O vínculo
          continua vindo daqui: vincular depois é o caminho que ninguém
          completa (Técnica da H10).
        */
        <NovaTarefa
          vinculo={{
            alvo: { tipo: 'cliente', id: conversa.cliente.id },
            rotulo: conversa.cliente.nome_exibicao ?? 'este contato',
          }}
          deOnde="Criada na tela de atendimento"
          aoCriar={() => definirRecado('Tarefa criada.')}
          aoFechar={() => definirCriandoTarefa(false)}
        />
      )}

      {recado && (
        <div className="recado" role="status" onAnimationEnd={() => definirRecado(null)}>
          {recado}
        </div>
      )}
    </div>
  );
}

/** Onde a escolha fica guardada. Preferência do aparelho, como o menu recolhido. */
const CHAVE_DO_PAINEL = 'cliqcrm:painel-do-cliente';

/**
 * O painel do cliente aberto ou recolhido, lembrado neste navegador.
 *
 * `localStorage` pela mesma razão do menu recolhido: é preferência de
 * APARELHO — no notebook a pessoa quer a conversa larga, no monitor grande
 * quer o cliente ao lado. A leitura e a escrita vão em `try`: janela privada e
 * dado de site bloqueado lançam, e o painel tem de abrir mesmo assim.
 */
function usePainelRecolhido(): [boolean, () => void] {
  const [fechado, definirFechado] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_DO_PAINEL) === 'fechado';
    } catch {
      return false;
    }
  });

  const alternar = useCallback(() => {
    definirFechado((atual) => {
      const proximo = !atual;
      try {
        localStorage.setItem(CHAVE_DO_PAINEL, proximo ? 'fechado' : 'aberto');
      } catch {
        // Sem armazenamento, a escolha vale até recarregar. Não é erro.
      }
      return proximo;
    });
  }, []);

  return [fechado, alternar];
}
